import type { ComponentType } from 'react'

import type { EntryOf, EntrySlice, RowProps } from '../types'
import {
  isArtistEntry,
  isBookEntry,
  isLabelEntry,
  isMovieEntry,
  isReleaseEntry,
} from './guard'
import type { MediaKind } from './meta'
import type { MediaDraft } from './write-spec'
import { BookRow } from './book-row'
import { MovieRow } from './movie-row'
import { ArtistRow, LabelRow, ReleaseRow } from './music-rows'

type MediaGuard<K extends MediaKind> = (
  entry: Parameters<EntrySlice<EntryOf[K], MediaDraft>['guard']>[0],
) => entry is EntryOf[K]

/**
 * One implementation, bound five times.
 *
 * This is what "five kinds, one slice" means once the types are honest (#359). The five kinds share
 * a draft, a row shape and a set of surfaces; what differs is the guard and the row component, so
 * those are the factory's parameters and nothing else is duplicated. Binding the five keys to the
 * `MediaEntry` union instead would let one literal serve all five and quietly erase every per-kind
 * type — it compiles, which is why it needed probing rather than arguing.
 */
export function mediaSlice<K extends MediaKind>(
  guard: MediaGuard<K>,
  row: ComponentType<RowProps<EntryOf[K], MediaDraft>>,
): EntrySlice<EntryOf[K], MediaDraft> {
  return { guard, row, editForm: null }
}

export const bookSlice = mediaSlice<'book'>(isBookEntry, BookRow)
export const movieSlice = mediaSlice<'movie'>(isMovieEntry, MovieRow)
export const artistSlice = mediaSlice<'artist'>(isArtistEntry, ArtistRow)
export const releaseSlice = mediaSlice<'release'>(isReleaseEntry, ReleaseRow)
export const labelSlice = mediaSlice<'label'>(isLabelEntry, LabelRow)
